import type { FC, ReactElement } from "react";
import React, { useEffect, useState } from "react";
import {
  Box,
  Drawer,
  DrawerContent,
  DrawerHeader,
} from "@edreamsodigeo/prisma-design-system";
import { useRouter, useTracking } from "@edreamsodigeo/frontend-core";

import { usePrimeSubscriptionModalUI } from "~/clientState/providers/PrimeSubscriptionModalProvider";
import { LightboxHeaderMobile } from "~/components/acquisition/Lightbox/LightboxRevamp/components/LightboxHeader";
import { LightboxFooter } from "~/components/acquisition/Lightbox/LightboxRevamp/components/LightboxFooter";
import { LightboxBenefitsMobile } from "~/components/acquisition/Lightbox/LightboxRevamp/components/LightboxBenefits";
import LightboxOffer from "~/components/acquisition/Lightbox/LightboxRevamp/components/LightboxOffer";
import { SubscriptionMultiOfferTabOffers } from "~/pages/MembershipPageMobile/SubscriptionMultiOffer/versions/common/contract";
import type { LightboxRevampProps } from "~/components/acquisition/Lightbox/LightboxRevamp/contracts";
import LightboxSavingsTable from "~/components/acquisition/Lightbox/LightboxRevamp/components/LightboxSavingsTable";
import {
  ActionsEnum,
  getCategoryLabel,
  trackingLabels,
} from "../config/tracking";

const LightboxRevampMobile: FC<LightboxRevampProps> = ({
  basicOffer,
  plusOffer,
  freeTrialDays,
}): ReactElement => {
  const { trackEvent } = useTracking();
  const { pathname } = useRouter();
  const { isOpen, close } = usePrimeSubscriptionModalUI();
  const [offerTab, setOfferTab] = useState(
    SubscriptionMultiOfferTabOffers.PLUS,
  );

  const onTabChange = (_event, selected): void => {
    setOfferTab(selected);
    trackEvent({
      category: getCategoryLabel(pathname),
      action: ActionsEnum.PRIME_LIGHTBOX,
      label: trackingLabels.onTabChanged({
        pathname: pathname,
        tabSelected: selected,
      }),
    });
  };

  const onCloseWithTrackingMobile = (): void => {
    trackEvent({
      category: getCategoryLabel(pathname),
      action: ActionsEnum.PRIME_LIGHTBOX,
      label: trackingLabels.onClose({
        pathname: pathname,
      }),
    });
    close();
  };

  useEffect(() => {
    if (isOpen) {
      trackEvent({
        category: getCategoryLabel(pathname),
        action: ActionsEnum.PRIME_LIGHTBOX,
        label: trackingLabels.onLoad({
          pathname: pathname,
        }),
      });
    }
  }, [isOpen, pathname, trackEvent]);

  return (
    <Drawer open={isOpen} size={Drawer.sizes.full} fixed>
      <DrawerHeader>
        <LightboxHeaderMobile onClose={onCloseWithTrackingMobile} />
      </DrawerHeader>
      <LightboxOffer
        offerTab={offerTab}
        onTabChange={onTabChange}
        basicOffer={basicOffer}
        plusOffer={plusOffer}
        freeTrialDays={freeTrialDays}
      />
      <DrawerContent>
        <Box mt={"minus_big"}>
          <LightboxBenefitsMobile
            showPrimePlus={offerTab === SubscriptionMultiOfferTabOffers.PLUS}
            extraElements={[
              {
                category: "lightboxPrimeRevampDesktop.table.section.title",
                element: <LightboxSavingsTable />,
                position: 1,
              },
            ]}
          />
          <LightboxFooter onClose={() => close()} />
        </Box>
      </DrawerContent>
    </Drawer>
  );
};

export default LightboxRevampMobile;
